/**
 * Ticket Card Component
 *
 * One ticket as a compact phone card: title, a chip row, and one caption line. `TicketCardContent`
 * is the body alone so the moderator list can hand it to DataTable's `renderCard` (DataTable owns
 * the card, the tap and the pager); `TicketCard` wraps it in its own card for the player lists.
 */

import { Card, CardActionArea, Box, Typography, Chip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { TicketSummaryDto, TicketTypeLabels } from '@api/types/tickets'
import { TicketStatusBadge } from './TicketStatusBadge'
import { TicketPriorityIndicator } from './TicketPriorityIndicator'
import { TicketTypeIcon } from './TicketTypeIcon'
import { TicketValidationChips } from './TicketValidationChips'
import { TicketEscalationChip } from './TicketEscalationChip'
import { normalizeId, ticketScore } from './ticketActions'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { useAuthStore } from '@store/authStore'

interface TicketCardContentProps {
  ticket: TicketSummaryDto
  /** Show who claimed it (the pool, where it may be somebody else's). */
  showAssignee?: boolean
}

export function TicketCardContent({ ticket, showAssignee = false }: TicketCardContentProps) {
  const { user } = useAuthStore()
  const claimedByOther = !!ticket.claimedByUserId && normalizeId(ticket.claimedByUserId) !== normalizeId(user?.userId)
  const score = ticketScore(ticket)
  const hasVotes = !!(ticket.upVotes || ticket.downVotes)

  // Everything that is plain information goes on one caption line, as words: the old card spread
  // it over icon-only rows whose meaning lived in hover titles.
  const meta = [
    TicketTypeLabels[ticket.type],
    `by ${ticket.createdByDisplayName || 'Unknown'}`,
    safeFormatDistanceToNow(ticket.lastUpdatedAt),
    `${ticket.commentCount} comment${ticket.commentCount === 1 ? '' : 's'}`,
    hasVotes ? `votes ${score > 0 ? `+${score}` : score}` : null,
    showAssignee && ticket.assignedToDisplayName ? `assigned to ${ticket.assignedToDisplayName}` : null,
  ].filter(Boolean)

  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
      <Box sx={{ pt: 0.25, flexShrink: 0 }}>
        <TicketTypeIcon type={ticket.type} size="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
          {ticket.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', my: 0.5 }}>
          <TicketStatusBadge status={ticket.status} />
          <TicketPriorityIndicator priority={ticket.priority} />
          <TicketEscalationChip level={ticket.escalationLevel} label={ticket.escalationLabel} paused={ticket.escalationPaused} hideOwner />
          {ticket.isEscalated && <Chip label="ESCALATED" size="small" color="error" sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />}
          {claimedByOther && (
            <Chip
              label={showAssignee && ticket.claimedByDisplayName ? `Claimed: ${ticket.claimedByDisplayName}` : 'CLAIMED'}
              size="small"
              color="warning"
              sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem', maxWidth: '100%' }}
            />
          )}
          <TicketValidationChips type={ticket.type} itemKey={ticket.itemKey} />
        </Box>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
          {meta.join(' · ')}
        </Typography>
      </Box>
    </Box>
  )
}

interface TicketCardProps {
  ticket: TicketSummaryDto
  onClick?: (ticketId: string) => void
  showAssignee?: boolean
}

export function TicketCard({ ticket, onClick, showAssignee = false }: TicketCardProps) {
  const navigate = useNavigate()

  const handleClick = () => {
    if (onClick) onClick(ticket.id)
    else navigate(`/support/tickets/${ticket.id}`)
  }

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardActionArea onClick={handleClick} sx={{ px: 1.5, py: 1.25 }}>
        <TicketCardContent ticket={ticket} showAssignee={showAssignee} />
      </CardActionArea>
    </Card>
  )
}
